import { type IPublicClientApplication } from '@azure/msal-browser';
import { useAppState } from '@Contexts/appContext';
import { EnumNavigationBar } from '@Contexts/navigation';
import { useSession } from '@Contexts/session';
import { useMenuFiltering } from '@Hooks/Navigation';
import { EnumPartnershipType } from '@Model';
import * as Sentry from '@sentry/react';
import { useLayoutEffect } from 'react';
import { createBrowserRouter, Outlet, RouteObject, RouterProvider, useNavigate } from 'react-router';
import App from '../App';
import { AppWrapper } from '../AppWrapper';
import { LoginGuard } from '../gui';
import { RouterErrorBoundary } from '../gui/components/errorHandling';
import { LoginPage } from '../gui/login/LoginPage';
import { Logout } from '../gui/Logout';
import { Signout } from '../gui/Signout';
import { setupChatRouting } from './setupChatRouting';
import { setupBillingRouting } from './setupBillingRouting';
import { setupContainerRentRouting } from './setupContainerRentRouting';
import { setupContainerStockRouting } from './setupContainerStockRouting';
import { setupDownloadRouting } from './setupDownloadRouting';
import { setupInvoiceSnapshotRouting } from './setupInvoiceSnapshotRouting';
import { setupOrderRouting } from './setupOrderRouting';
import { setupPermissionsRouting } from './setupPermissionsRouting';
import { setupPortalRouting } from './setupPortalRouting';
import { setupProfileRouting } from './setupProfileRouting';
import { setupSettingsRouting } from './setupSettingsRouting';
import { setupStammdatenRoutes } from './setupStammdatenRouting';

const NO_ACCESS_FALLBACK = '/profile/me';

const PREFERRED_LANDING: Record<EnumPartnershipType, string> = {
    [EnumPartnershipType.Portal]: '/home',
    [EnumPartnershipType.Internal]: '/weighing',
};

// @ai-generated 2026-06-12T11:59:53+00:00 by philipp.kammerer@synqony.com using opus-4.8
function DefaultLandingRedirect() {
    const {
        profile: { permissions },
    } = useAppState();
    const {
        current: { subpath, partnershipType },
    } = useSession();
    const menu = useMenuFiltering(EnumNavigationBar.Main, permissions);
    const navigate = useNavigate();

    const allowedLinks = menu.flatMap((element) =>
        element.kind === 'leaf' ? [element.link] : element.targets.map((t) => t.link),
    );
    const preferred = PREFERRED_LANDING[partnershipType];

    const target = (preferred && allowedLinks.includes(preferred) ? preferred : allowedLinks[0]) ?? NO_ACCESS_FALLBACK;

    useLayoutEffect(() => {
        navigate(`/${subpath}${target}`, { replace: true });
    }, [navigate, subpath, target]);

    return null;
}

const synqoCloudModules: RouteObject[] = [
    { path: '', element: <DefaultLandingRedirect /> },
    setupPortalRouting(),
    setupStammdatenRoutes(),
    setupOrderRouting(),
    setupContainerStockRouting(),
    setupContainerRentRouting(),
    setupSettingsRouting(),
    setupBillingRouting(),
    setupInvoiceSnapshotRouting(),
    setupPermissionsRouting(),
    setupChatRouting(),
    setupProfileRouting(),
    setupDownloadRouting(),
];

export function SynqoCloudRouter({ pca }: { pca: IPublicClientApplication }) {
    const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

    const SynqoCloudRouter = sentryCreateBrowserRouter([
        {
            path: '',
            element: <AppWrapper pca={pca} />,
            children: [
                {
                    path: '',
                    element: (
                        <LoginGuard loginPageRoute="/login">
                            <Outlet />
                        </LoginGuard>
                    ),
                    errorElement: <RouterErrorBoundary />,
                    children: [
                        {
                            path: ':subpath',
                            element: <App />,
                            children: synqoCloudModules,
                        },
                    ],
                },
                {
                    path: 'login',
                    element: <LoginPage />,
                },
                {
                    path: 'logout',
                    element: <Logout />,
                },
                {
                    path: 'signout',
                    element: <Signout />,
                },
            ],
        },
    ]);

    return <RouterProvider router={SynqoCloudRouter} />;
}
